// Página de REGISTRO (alta pública). Captura nombre, email, contraseña (con la
// política de AWS Cognito evaluada en vivo) y el plan preseleccionado desde los
// parámetros de la URL (?plan=&billing=), que llega desde la página de Planes.
//
// NOTA DE INTEGRACIÓN: hoy el alta de cuentas es por invitación (el backend crea
// owners vía /admin/owners). Esta pantalla deja el flujo de auto-registro LISTO
// en el frontend; la llamada real de sign-up y el checkout de la pasarela de
// pago se conectarán en una fase posterior (ver `submitRegistration`). Mientras
// tanto, el formulario valida y muestra un estado de confirmación.
// SEO: noindex (páginas de conversión no deben competir en SERP) + título.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/components.js';
import { useLanguage } from '../../i18n/LanguageProvider.js';
import { SUPPORTED_LOCALES } from '../../i18n/locales.js';
import { CognitoError, confirmSignUp, login, resendSignUpCode, signUp } from '../../auth/cognito.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiClient } from '../../api/client.js';
import { createApi } from '../../api/endpoints.js';
import { config, hasCognitoConfig } from '../../config.js';
import { Container, Eyebrow } from '../components.js';
import { useSeo } from '../seo.js';
import {
  fetchLegalDocuments,
  fetchPublicPlans,
  recordLegalConsent,
  type PublicLegalDocument,
  type PublicPlan,
} from '../publicApi.js';
import { annualSavingPercent, chargeTotalCents, formatPrice, monthlyEquivalentCents } from '../planPricing.js';
import { useAuthMessages } from '../../auth/useAuthMessages.js';
import { SocialLoginButtons, SocialDivider } from '../../auth/SocialLoginButtons.js';
import { trackEvent } from '../../analytics/analytics.js';

export function RegisterView() {
  const [params] = useSearchParams();
  const planParam = params.get('plan') ?? 'free';
  const billingParam = params.get('billing') === 'monthly' ? 'monthly' : 'annual';

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { passwordPolicy, signUpError } = useAuthMessages();
  const { locale, setLocale } = useLanguage();
  const { setSession } = useAuth();
  // Idioma elegido en el formulario (inicia con el idioma activo de la UI).
  const [registerLocale, setRegisterLocale] = useState(locale);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Planes REALES leídos de la BD vía `GET /public/plans` (mismo origen que la
  // página de precios), para que el desplegable nunca quede desalineado.
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [plan, setPlan] = useState(planParam);
  // Cadencia de facturación ELEGIBLE en esta pantalla (mensual/anual). Se
  // inicializa con el `?billing=` de la URL (viene de la página de Planes) pero
  // ahora es editable aquí: el usuario que llega directo a /registro también
  // puede escoger cómo quiere que le facturen y ver el importe antes de pagar.
  const [billing, setBilling] = useState<'monthly' | 'annual'>(billingParam);
  const [accept, setAccept] = useState(false);
  // Versiones VIGENTES de los documentos legales que el usuario acepta. Se
  // cargan del backend para registrar la aceptación de la versión EXACTA vigente
  // en el momento del alta (log transaccional).
  const [legalDocs, setLegalDocs] = useState<PublicLegalDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Paso de confirmación por código (si el pool exige verificar el correo).
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  useSeo({
    title: t('register.seoTitle'),
    description: t('register.seoDescription'),
    path: '/registro',
    noindex: true,
  });

  // Carga el catálogo real de planes y ajusta la preselección: si el `?plan=`
  // de la URL no corresponde a un plan existente, cae al plan por defecto.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchPublicPlans(ctrl.signal)
      .then((items) => {
        const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
        setPlans(sorted);
        const codes = new Set(sorted.map((p) => p.code));
        const fallback = sorted.find((p) => p.is_default)?.code ?? sorted[0]?.code ?? '';
        setPlan((current) => (codes.has(current) ? current : fallback));
      })
      .catch(() => {
        // Si falla la carga (p. ej. sin red), dejamos el desplegable vacío;
        // el registro sigue siendo posible y el backend asigna el plan por defecto.
      });
    return () => ctrl.abort();
  }, []);

  // Carga las versiones vigentes de los documentos legales (para registrar la
  // aceptación de la versión exacta en el alta).
  useEffect(() => {
    const ctrl = new AbortController();
    fetchLegalDocuments(ctrl.signal)
      .then((docs) => setLegalDocs(docs))
      .catch(() => {
        // Sin red/documentos: el alta no se bloquea, pero el consentimiento no se
        // podrá registrar hasta que el endpoint esté disponible (ver submit).
      });
    return () => ctrl.abort();
  }, []);

  /** Plan seleccionado (objeto completo) y si es el gratuito por defecto. */
  const selectedPlan = plans.find((p) => p.code === plan) ?? null;
  const isFreePlan = selectedPlan?.is_default ?? plan === 'free';
  const planName = selectedPlan?.name ?? plan;

  const policy = useMemo(() => passwordPolicy(password), [password, passwordPolicy]);
  const policyMet = policy.filter((r) => r.ok).length;
  // Etiqueta de versión mostrada junto a la casilla de aceptación: si todos los
  // documentos comparten versión se muestra una; si difieren, se omite.
  const legalVersionLabel = useMemo(() => {
    const versions = [...new Set(legalDocs.map((d) => d.version))];
    return versions.length === 1 ? versions[0] : '';
  }, [legalDocs]);
  const canSubmit = name.trim() !== '' && /\S+@\S+\.\S+/.test(email) && policyMet === policy.length && accept;

  async function submitRegistration(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Alta real contra Cognito. `signUp` devuelve UsernameExistsException si el
      // correo ya está registrado, lo que bloquea el duplicado (ver manejo abajo).
      const { needsConfirmation } = await signUp(email.trim(), password, name.trim(), registerLocale);

      // LOG TRANSACCIONAL de la aprobación: registra que ESTE correo aceptó las
      // versiones VIGENTES de los documentos legales en el momento del alta. Se
      // identifica por email (la cuenta aún no existe en el backend); el servidor
      // lo enlazará al usuario cuando se materialice. Best-effort: un fallo aquí
      // no debe tirar el alta ya realizada en Cognito, pero sí se informa.
      const versionIds = legalDocs.map((d) => d.version_id).filter((id) => id);
      if (versionIds.length > 0) {
        try {
          await recordLegalConsent({
            email: email.trim(),
            versionIds,
            method: 'signup_checkbox',
            locale: registerLocale,
          });
        } catch {
          // No bloquea el alta; el consentimiento puede re-registrarse al primer
          // inicio de sesión si fuese necesario (ver roadmap de re-aceptación).
        }
      }

      // Si el pool exige verificar el correo, pasamos al paso de código; la
      // continuación (login automático + checkout) ocurre tras confirmar. Si el
      // pool no exige confirmación (p. ej. auto-verificado), continuamos ya.
      if (needsConfirmation) {
        setNeedsConfirm(true);
        setInfo(t('register.codeSentInfo', { email: email.trim() }));
      } else {
        await finishAndContinue();
      }
    } catch (err) {
      setError(signUpError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirmation(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim().length < 4) return;
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp(email.trim(), code.trim());
      await finishAndContinue();
    } catch (err) {
      setError(
        err instanceof CognitoError && err.type === 'CodeMismatchException'
          ? t('auth.errors.codeMismatchSignup')
          : signUpError(err),
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Cierra el alta: inicia sesión automáticamente con las credenciales recién
   * creadas y encamina al usuario según el plan elegido:
   *   - Plan de PAGO  → `/billing?checkout=auto&plan=<code>&period=<...>`, que
   *     dispara el checkout de Stripe del plan elegido (precio tomado de la BD).
   *   - Plan GRATIS   → directo a la app (`/`).
   *
   * El login automático puede fallar de forma transitoria (p. ej. el trigger
   * PostConfirmation aún está materializando la cuenta); en ese caso NO se pierde
   * nada: se cae a la pantalla "cuenta lista" con el botón de iniciar sesión.
   */
  async function finishAndContinue(): Promise<void> {
    // MEDICIÓN DE CONVERSIÓN (RA-01): el alta por CORREO se ha completado aquí
    // (la cuenta ya existe en Cognito). `finishAndContinue` es la ÚNICA costura
    // común a los dos sub-caminos del alta por correo —`submitRegistration` sin
    // verificación y `submitConfirmation` tras el código— que son MUTUAMENTE
    // EXCLUYENTES, así que corre exactamente una vez por cuenta nueva. Se emite
    // ANTES del login automático: la conversión ya ocurrió y el login posterior
    // puede fallar de forma transitoria (carrera con PostConfirmation); emitir
    // antes evita perder una conversión real por un fallo de login. `trackEvent`
    // es best-effort (no-op sin GA/consentimiento, nunca lanza) y no bloquea.
    trackEvent('sign_up', { method: 'email' });
    const period = billing === 'monthly' ? 'monthly' : 'yearly';
    const target = isFreePlan
      ? '/'
      : `/billing?checkout=auto&plan=${encodeURIComponent(plan)}&period=${period}`;
    try {
      const outcome = await login(email.trim(), password);
      // Un alta recién confirmada nunca produce el desafío NEW_PASSWORD_REQUIRED
      // (ese es para invitados de AdminCreateUser), pero si llegara, caemos al
      // flujo manual de login.
      if ('tokens' in outcome) {
        // CONDICIÓN DE CARRERA: el trigger PostConfirmation de Cognito crea la
        // fila `app_user` + tenant de forma ASÍNCRONA. Si navegáramos ya, la
        // primera llamada autenticada (p. ej. /billing/checkout) podría llegar
        // antes de que exista la cuenta y el authorizer respondería 401
        // (unknown_user), cerrando la sesión. Por eso ESPERAMOS a que la cuenta
        // esté lista sondeando /me/permissions con reintentos cortos antes de
        // guardar la sesión y navegar.
        await waitForAccountReady(outcome.tokens.idToken);
        // Persiste el idioma elegido en el alta: será la preferencia GUARDADA
        // del usuario y prevalecerá en los siguientes inicios de sesión (i18n).
        await persistRegisterLocale(outcome.tokens.idToken);
        setSession(outcome.tokens, email.trim());
        navigate(target, { replace: true });
        return;
      }
    } catch {
      // Login automático no disponible todavía: mostramos la pantalla de éxito
      // con el enlace para iniciar sesión manualmente (abajo), que también
      // encadena el checkout vía `?next=` para los planes de pago.
    }
    setDone(true);
  }

  /**
   * Sondea `GET /me/permissions` hasta que la cuenta exista (PostConfirmation la
   * provisiona de forma asíncrona tras confirmar en Cognito). Reintenta ante 401
   * `unknown_user` con backoff corto. Lanza si no está lista tras los intentos,
   * para que el llamador caiga al login manual en vez de perder la sesión.
   */
  async function waitForAccountReady(idToken: string): Promise<void> {
    const client = new ApiClient(config.apiUrl, () => idToken);
    const api = createApi(client);
    const maxAttempts = 6;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        await api.me.permissions();
        return; // la cuenta ya existe y responde.
      } catch {
        // 401 (unknown_user) o red: esperar y reintentar. El cliente ya reintenta
        // red/5xx internamente; aquí cubrimos el 401 por cuenta aún no creada.
        await new Promise((r) => setTimeout(r, 500 + attempt * 500));
      }
    }
    throw new Error('account_not_ready');
  }

  /**
   * Guarda el idioma elegido en el alta como preferencia del usuario (i18n).
   * Best-effort: un fallo no debe interrumpir el flujo de registro/checkout; el
   * usuario siempre puede cambiarlo luego desde su cuenta.
   */
  async function persistRegisterLocale(idToken: string): Promise<void> {
    try {
      const client = new ApiClient(config.apiUrl, () => idToken);
      await createApi(client).me.setLocale(registerLocale);
    } catch {
      // Ignorado a propósito (ver doc del método).
    }
  }

  async function resendCode() {
    setError(null);
    setInfo(null);
    try {
      await resendSignUpCode(email.trim());
      setInfo(t('register.codeResentInfo'));
    } catch (err) {
      setError(signUpError(err));
    }
  }

  if (done) {
    return (
      <Container className="py-space-xl lg:py-20 max-w-xl flex flex-col items-center text-center gap-space-md">
        <span className="w-16 h-16 rounded-full bg-goldmine-mint/15 border border-goldmine-mint/40 flex items-center justify-center">
          <Icon name="mark_email_read" className="text-goldmine-mint text-[32px]" />
        </span>
        <h1 className="font-headline-lg text-headline-lg-mobile lg:text-headline-lg text-on-surface font-bold">
          {t('register.readyTitle')}
        </h1>
        <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
          {t('register.readyBodyPre')}
          <strong className="text-on-surface">{planName}</strong>
          {t('register.readyBodyWithEmail')}
          <strong className="text-on-surface">{email}</strong>
          {isFreePlan ? t('register.readyBodyFree') : t('register.readyBodyPaid')}
        </p>
        <div className="flex flex-col sm:flex-row gap-space-sm mt-space-sm">
          <button
            type="button"
            onClick={() =>
              navigate(
                isFreePlan
                  ? '/login'
                  : `/login?next=${encodeURIComponent(
                      `/billing?checkout=auto&plan=${plan}&period=${billing === 'monthly' ? 'monthly' : 'yearly'}`,
                    )}`,
              )
            }
            className="btn-cyan"
          >
            <Icon name="login" className="text-[18px]" /> {t('marketing.login')}
          </button>
          <Link to="/" className="btn-secondary">
            {t('register.backToHome')}
          </Link>
        </div>
      </Container>
    );
  }

  // Paso de verificación por código (si el pool exige confirmar el correo).
  if (needsConfirm) {
    return (
      <Container className="py-space-xl lg:py-20 max-w-md flex flex-col gap-space-md">
        <div className="flex flex-col items-center text-center gap-space-sm">
          <span className="w-16 h-16 rounded-full bg-primary-container/15 border border-primary-container/40 flex items-center justify-center">
            <Icon name="mark_email_unread" className="text-primary text-[32px]" />
          </span>
          <h1 className="font-headline-lg text-headline-lg-mobile text-on-surface font-bold">
            {t('register.verifyTitle')}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
            {t('register.verifyBody', { email })}
          </p>
        </div>
        {info && (
          <div className="p-space-sm rounded-lg bg-primary-container/15 border border-primary-container/40 text-primary font-body-sm text-body-sm">
            {info}
          </div>
        )}
        {error && (
          <div className="p-space-sm rounded-lg bg-error-container/20 border border-error/40 text-error font-body-sm text-body-sm flex items-start gap-space-xs">
            <Icon name="error" className="text-[18px] shrink-0" />
            {error}
          </div>
        )}
        <form onSubmit={submitConfirmation} className="flex flex-col gap-space-md">
          <input
            className="w-full bg-surface-deck border border-border-subtle rounded-lg py-3 px-space-md text-on-surface font-code-catalog text-code-catalog tracking-widest text-center focus:outline-none focus:border-border-focus"
            value={code}
            inputMode="numeric"
            maxLength={8}
            placeholder={t('register.verifyCodePlaceholder')}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <button type="submit" className="btn-cyan w-full" disabled={busy || code.trim().length < 4}>
            <Icon name="check_circle" className="text-[20px]" />
            {busy ? t('register.verifying') : t('register.activateAccount')}
          </button>
          <button type="button" onClick={() => void resendCode()} className="font-body-sm text-body-sm text-primary hover:underline">
            {t('register.resendCode')}
          </button>
        </form>
      </Container>
    );
  }

  return (
    <Container className="py-space-xl lg:py-16">
      <div className="grid lg:grid-cols-2 gap-space-xl items-start max-w-5xl mx-auto">
        {/* Columna de valor */}
        <div className="hidden lg:flex flex-col gap-space-lg sticky top-24">
          <Eyebrow icon="rocket_launch">{t('register.valueEyebrow')}</Eyebrow>
          <h1 className="font-display-hero text-display-hero-mobile text-on-surface font-bold tracking-tight text-balance">
            {t('register.valueTitlePre')}<span className="text-primary">{t('register.valueTitleHighlight')}</span>
          </h1>
          <ul className="flex flex-col gap-space-md">
            {[
              { icon: 'photo_camera', t: t('register.benefitPhoto') },
              { icon: 'auto_awesome', t: t('register.benefitAuto') },
              { icon: 'query_stats', t: t('register.benefitValue') },
              { icon: 'credit_card_off', t: t('register.benefitNoCard') },
            ].map((b) => (
              <li key={b.t} className="flex items-center gap-space-sm font-body-md text-body-md text-on-surface">
                <span className="w-9 h-9 rounded-lg bg-primary-container/10 border border-primary-container/30 text-primary flex items-center justify-center">
                  <Icon name={b.icon} className="text-[20px]" />
                </span>
                {b.t}
              </li>
            ))}
          </ul>
        </div>

        {/* Formulario */}
        <div className="surface-card flex flex-col gap-space-md">
          <h2 className="font-headline-sm text-headline-md text-on-surface font-bold">{t('register.formTitle')}</h2>

          {!hasCognitoConfig() && (
            <div className="p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm flex items-start gap-space-xs">
              <Icon name="warning" className="text-[18px] shrink-0" />
              {t('register.notConfigured')}
            </div>
          )}

          {error && (
            <div className="p-space-sm rounded-lg bg-error-container/20 border border-error/40 text-error font-body-sm text-body-sm flex items-start gap-space-xs">
              <Icon name="error" className="text-[18px] shrink-0" />
              {error}
            </div>
          )}

          {/* Alta rápida con Google / Apple (convive con el formulario). El
              plan/checkout de pago se gestiona tras entrar; el registro social
              crea la cuenta con el plan por defecto (como el auto-registro). */}
          <SocialLoginButtons returnTo="/" />
          <SocialDivider />

          <form onSubmit={submitRegistration} className="flex flex-col gap-space-md">
            <div className="flex flex-col gap-space-xs">
              <label htmlFor="name" className="field-label">{t('register.nameLabel')}</label>
              <div className="field-slot">
                <Icon name="person" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <input id="name" className="field-input" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} required />
              </div>
            </div>

            <div className="flex flex-col gap-space-xs">
              <label htmlFor="email" className="field-label">{t('register.emailLabel')}</label>
              <div className="field-slot">
                <Icon name="alternate_email" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <input id="email" type="email" className="field-input" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>

            <div className="flex flex-col gap-space-xs">
              <label htmlFor="pw" className="field-label">{t('register.passwordLabel')}</label>
              <div className="field-slot pr-space-xs">
                <Icon name="key" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <input
                  id="pw"
                  type={showPassword ? 'text' : 'password'}
                  className="field-input"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-on-surface-variant hover:text-primary transition-colors"
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} className="text-[18px]" />
                </button>
              </div>
              {/* Checklist de política */}
              <ul className="flex flex-col gap-0.5 mt-space-xs">
                {policy.map((rule) => (
                  <li key={rule.label} className="flex items-center gap-space-xs font-body-sm text-body-sm">
                    <Icon name={rule.ok ? 'check_circle' : 'radio_button_unchecked'} className={`text-[15px] ${rule.ok ? 'text-goldmine-mint' : 'text-outline'}`} />
                    <span className={rule.ok ? 'text-goldmine-mint' : 'text-outline'}>{rule.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-space-xs">
              <label htmlFor="locale" className="field-label">{t('register.languageLabel')}</label>
              <div className="field-slot">
                <Icon name="language" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select
                  id="locale"
                  className="field-input"
                  value={registerLocale}
                  onChange={(e) => {
                    const next = e.target.value as typeof registerLocale;
                    setRegisterLocale(next);
                    // Cambia el idioma de la UI al instante (y lo recuerda local).
                    setLocale(next);
                  }}
                >
                  {SUPPORTED_LOCALES.map((l) => (
                    <option key={l.code} value={l.code} lang={l.tag}>
                      {l.nativeName}
                    </option>
                  ))}
                </select>
              </div>
              <span className="font-badge-micro text-badge-micro text-on-surface-variant">
                {t('register.languageHint')}
              </span>
            </div>

            <div className="flex flex-col gap-space-xs">
              <label htmlFor="plan" className="field-label">{t('register.planLabel')}</label>
              <div className="field-slot">
                <Icon name="workspace_premium" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select id="plan" className="field-input" value={plan} onChange={(e) => setPlan(e.target.value)} disabled={plans.length === 0}>
                  {plans.length === 0 ? (
                    <option value="">{t('register.plansLoading')}</option>
                  ) : (
                    plans.map((p) => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))
                  )}
                </select>
              </div>

              {/* Selección de cadencia + precio: solo tiene sentido para planes
                  de PAGO. El plan gratuito no cobra, así que se omite y se
                  muestra una nota breve en su lugar. */}
              {!isFreePlan && selectedPlan && (
                <div className="flex flex-col gap-space-xs mt-space-xs rounded-lg border border-border-subtle bg-surface-deck p-space-sm">
                  {/* Conmutador mensual / anual (mismo patrón que la página de Planes). */}
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface-rack border border-border-subtle self-start">
                    <button
                      type="button"
                      onClick={() => setBilling('monthly')}
                      aria-pressed={billing === 'monthly'}
                      className={`px-3 h-8 rounded-full font-body-sm text-body-sm transition-colors ${
                        billing === 'monthly' ? 'bg-surface-base text-on-surface' : 'text-on-surface-variant'
                      }`}
                    >
                      {t('register.monthly')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBilling('annual')}
                      aria-pressed={billing === 'annual'}
                      className={`relative px-3 h-8 rounded-full font-body-sm text-body-sm transition-colors ${
                        billing === 'annual' ? 'bg-surface-base text-on-surface' : 'text-on-surface-variant'
                      }`}
                    >
                      {t('register.annual')}
                      {annualSavingPercent(selectedPlan) > 0 && (
                        <span className="absolute -top-2 -right-1.5 flex items-center gap-0.5 font-badge-micro text-badge-micro font-bold text-surface-base uppercase tracking-wider bg-goldmine-mint rounded-full px-1.5 py-0.5 whitespace-nowrap">
                          <Icon name="savings" className="text-[11px]" />
                          -{annualSavingPercent(selectedPlan)}%
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Precio: equivalente por mes (destacado) + total que se cobra. */}
                  <div className="flex items-baseline gap-space-xs">
                    <span className="font-headline-sm text-headline-sm text-on-surface font-bold">
                      {formatPrice(monthlyEquivalentCents(selectedPlan, billing), selectedPlan.currency)}
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">{t('register.perMonth')}</span>
                  </div>
                  <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider">
                    {billing === 'annual'
                      ? t('register.chargedYearly', {
                          amount: formatPrice(chargeTotalCents(selectedPlan, billing), selectedPlan.currency),
                        })
                      : t('register.chargedMonthly', {
                          amount: formatPrice(chargeTotalCents(selectedPlan, billing), selectedPlan.currency),
                        })}
                    {t('register.paymentAfterVerify')}
                  </span>
                </div>
              )}
              {isFreePlan && selectedPlan && (
                <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider">
                  {t('register.freePlanNote')}
                </span>
              )}
            </div>

            <label
              htmlFor="register-accept-legal"
              className="flex items-start gap-space-xs font-body-sm text-body-sm text-on-surface-variant"
            >
              {/* Casilla SIN pre-marcar + acción afirmativa (clickwrap enforceable).
                  id + htmlFor explícitos y foco visible (WCAG 2.2 AA). */}
              <input
                type="checkbox"
                id="register-accept-legal"
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="mt-0.5 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              />
              <span>
                {t('register.acceptPre')}
                <Link to="/legal/terminos" target="_blank" className="text-primary hover:underline">
                  {t('register.acceptTerms')}
                </Link>
                {t('register.acceptMid')}
                <Link to="/legal/aviso-legal" target="_blank" className="text-primary hover:underline">
                  {t('register.acceptLegalNotice')}
                </Link>
                {t('register.acceptAnd')}
                <Link to="/legal/privacidad" target="_blank" className="text-primary hover:underline">
                  {t('register.acceptPrivacy')}
                </Link>
                {legalVersionLabel ? t('register.acceptVersion', { version: legalVersionLabel }) : ''}.
              </span>
            </label>

            <button type="submit" className="btn-cyan w-full" disabled={!canSubmit || busy}>
              <Icon name="rocket_launch" className="text-[20px]" />
              {busy ? t('register.submitBusy') : t('register.submit')}
            </button>

            <p className="text-center font-body-sm text-body-sm text-on-surface-variant">
              {t('register.haveAccount')}{' '}
              <Link to="/login" className="text-primary hover:underline">{t('marketing.login')}</Link>
            </p>
          </form>
        </div>
      </div>
    </Container>
  );
}
